import { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, type DriverProfile } from '../../lib/auth';
import { useSetPageHeader } from '../../components/app/AppShell';
import { DocPicker } from '../../components/form/DocPicker';
import { apiFetch } from '../../lib/api-client';
import { invalidateDoc } from '../../lib/doc-cache';
import { loadKycDraft, saveKycDraft, clearKycDraft } from '../../lib/kyc-draft';
import { haptic } from '../../lib/haptic';

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const errorStyle: CSSProperties = { color: '#B91C1C', fontSize: 12, margin: 0 };

export function KycPanEditRoute() {
  const { t } = useTranslation('profile');
  useSetPageHeader({ backTo: '/profile/kyc', title: t('kyc.panTitle') }, []);
  const navigate = useNavigate();
  const driver = useAuth(s => s.driver);

  const [panNumber, setPanNumber] = useState(() => loadKycDraft<{ panNumber?: string }>(driver?.id, 'pan')?.panNumber ?? driver?.panNumber ?? '');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [touched, setTouched] = useState<{ num: boolean; photo: boolean }>({ num: false, photo: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = loadKycDraft<{ panNumber?: string }>(driver?.id, 'pan');
    setPanNumber(d?.panNumber ?? driver?.panNumber ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed (draft-aware) on driver identity change
  }, [driver?.id]);

  // Persist the typed value as a draft so a background/return doesn't lose it (#500).
  useEffect(() => {
    if (driver?.id && touched.num) saveKycDraft(driver.id, 'pan', { panNumber });
  }, [panNumber, touched.num, driver?.id]);

  const panValid = panNumber.length === 0 || PAN_REGEX.test(panNumber);
  const canSave = panValid && !busy;

  const handleSave = async () => {
    if (!driver) return;
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      if (touched.num) payload.panNumber = panNumber;
      if (touched.photo && photoDataUrl) payload.panUrl = photoDataUrl;

      try {
        await apiFetch('/driver-app/me/kyc', {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } catch (e) {
        if ((e as { status?: number })?.status === 401) {
          navigate('/auth', { replace: true });
          return;
        }
        throw e;
      }
      const updated = await apiFetch<DriverProfile>('/driver-app/me');
      useAuth.setState({ driver: updated });
      await invalidateDoc('pan');
      clearKycDraft(driver.id, 'pan');
      haptic.success();
      navigate('/profile/kyc');
    } catch (e: unknown) {
      const errMsg = (e as { message?: string } | null | undefined)?.message;
      setError(errMsg || t('kyc.submitError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 pt-2 pb-10">
      <div className="space-y-3 mb-4">
        <div>
          <label htmlFor="pan-input" className="block text-sm font-semibold mb-1">{t('signupFields.panNumber', { ns: 'auth' })}</label>
          <input
            id="pan-input"
            type="text"
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            value={panNumber}
            onChange={(e) => { setPanNumber(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 10)); setTouched(p => ({ ...p, num: true })); }}
            className="w-full px-3 py-3 border border-[var(--color-border)] rounded-[var(--radius-md)] text-base"
          />
          {panNumber.length > 0 && !panValid ? <p style={errorStyle}>{t('signupValidation.panInvalid', { ns: 'auth', defaultValue: 'PAN must match the ABCDE1234F format' })}</p> : null}
        </div>

        <DocPicker
          value={photoDataUrl}
          onChange={(dataUrl) => { setPhotoDataUrl(dataUrl); setTouched(p => ({ ...p, photo: true })); }}
          label={driver?.hasPan ? t('kyc.photoOnFile') : t('kyc.noPhotoYet')}
          accept="image/*,application/pdf"
        />
      </div>

      {error ? <p role="alert" style={{ ...errorStyle, margin: '0 0 0.5rem' }}>{error}</p> : null}

      <button
        type="button"
        disabled={!canSave}
        onClick={() => { haptic.select(); handleSave(); }}
        className="w-full py-3 rounded-full font-bold disabled:opacity-50"
        style={{ background: 'var(--color-btn-primary)', color: '#fff', minHeight: 'var(--touch-target, 52px)' }}
      >
        {busy ? t('kyc.save') + '…' : t('kyc.save')}
      </button>
    </div>
  );
}
